import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Landmark, CalendarDays, ArrowLeft, ShieldCheck } from "lucide-react";
import { BrandMark, useBranding } from "../branding";
import { PdfButton } from "../PdfButton";
import { PdfViewer } from "../PdfViewer";
import { PublicServiceHeader } from "../PublicServiceHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;

function PortalCourtName() {
  const brand = useBranding();
  return <>{brand.court_name}</>;
}

const Badge = ({ v }) => (
  <span className="inline-block text-[10px] font-mono font-semibold uppercase tracking-wide border border-stone-300 bg-stone-100 text-stone-700 rounded-sm px-1.5 py-0.5">{v}</span>
);

function Shell({ children }) {
  return (
    <div className="court-public-shell min-h-screen text-slate-900" data-testid="court-public-portal">
      <PublicServiceHeader court subtitle="Public access · Fictional demonstration" testId="portal-court" actions={<PdfButton testId="portal-pdf-btn" publicMode rootSelector="#portal-main" />} />
      <nav className="court-public-nav" aria-label="Public services" data-testid="court-public-navigation"><Link to="/portal" data-testid="portal-home">Court records</Link><Link to="/custody-locator" data-testid="portal-custody-locator">Custody locator</Link><Link to="/background-check" data-testid="portal-background-service">Background checks</Link><Link to="/foid" data-testid="portal-foid-service">FOID services</Link></nav>
      <main className="max-w-5xl mx-auto px-4 py-8" id="portal-main">{children}</main>
      <PdfViewer />
      <footer className="max-w-5xl mx-auto px-4 pb-8 text-[11px] text-stone-400">
        Sealed, confidential and juvenile records are excluded by law. All content is fictional demonstration data.
      </footer>
    </div>
  );
}

function CitationPay() {
  const [f, setF] = useState({ citation_number: "", last_name: "" });
  const [cit, setCit] = useState(null);
  const [pay, setPay] = useState({ payer_name: "", card: "" });
  const [paid, setPaid] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const lookup = async (e) => {
    e?.preventDefault();
    setErr(null); setPaid(null); setCit(null);
    try { setCit((await axios.post(`${API}/portal/citations/lookup`, f)).data); }
    catch (ex) { setErr(ex.response?.data?.detail || "Lookup failed"); }
  };
  const payNow = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await axios.post(`${API}/portal/citations/pay`, { ...f, payer_name: pay.payer_name, card_last4: pay.card.slice(-4) });
      setPaid(r.data);
      setCit({ ...cit, status: "PAID", receipt_number: r.data.receipt_number });
    } catch (ex) { setErr(ex.response?.data?.detail || "Payment failed"); }
    setBusy(false);
  };
  return (
    <div className="bg-white border border-stone-200 rounded-md p-6 shadow-sm mt-6" data-testid="portal-citation-panel">
      <h2 className="text-xl font-semibold tracking-tight mb-1">Pay a Traffic Citation</h2>
      <p className="text-sm text-stone-500 mb-4">Look up your ticket with the citation number and your last name. Fines can be paid online instantly (demonstration payment — no real charge).</p>
      <form onSubmit={lookup} className="flex flex-wrap gap-2">
        <input className="border border-stone-300 rounded-sm px-3 py-2 text-sm w-56" placeholder="Citation No. e.g. CIT-2026-000001"
          value={f.citation_number} onChange={(e) => setF({ ...f, citation_number: e.target.value.toUpperCase() })} data-testid="portal-cit-number" />
        <input className="border border-stone-300 rounded-sm px-3 py-2 text-sm w-44" placeholder="Last name"
          value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="portal-cit-lastname" />
        <button type="submit" className="bg-stone-900 text-white text-sm font-medium px-4 py-2 rounded-sm hover:bg-stone-700" data-testid="portal-cit-lookup-btn">Look Up</button>
      </form>
      {err && <div className="mt-3 text-sm text-red-700" data-testid="portal-cit-error">{err}</div>}
      {cit && (
        <div className="mt-4 border border-stone-200 rounded-sm p-4" data-testid="portal-cit-result">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono font-bold text-stone-900">{cit.citation_number}</span>
            <span className="text-sm">{cit.first_name} {cit.last_name}</span>
            <span className="text-xs text-stone-500">{cit.violation_code} — {cit.violation_label}</span>
            <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-sm ${cit.status === "PAID" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid="portal-cit-status">{cit.status}</span>
          </div>
          <div className="mt-2 text-sm text-stone-600">
            {cit.must_appear
              ? <>Court appearance required{cit.court_date ? <> on <b>{cit.court_date} at 9:00 AM</b></> : null}. The fine will be set by the Court and cannot be paid online.</>
              : <>Fine due: <b>${Number(cit.fine).toFixed(2)}</b> · Case {cit.case_number} · Issued {cit.issued}</>}
          </div>
          {cit.status === "PAID" && cit.receipt_number && (
            <div className="mt-2 text-sm text-emerald-700 font-medium" data-testid="portal-cit-paid">Paid in full — Receipt {cit.receipt_number}</div>)}
          {!cit.must_appear && cit.status !== "PAID" && (
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <input className="border border-stone-300 rounded-sm px-3 py-2 text-sm w-52" placeholder="Cardholder name"
                value={pay.payer_name} onChange={(e) => setPay({ ...pay, payer_name: e.target.value })} data-testid="portal-cit-payer" />
              <input className="border border-stone-300 rounded-sm px-3 py-2 text-sm w-48" placeholder="Card number (demo)"
                value={pay.card} onChange={(e) => setPay({ ...pay, card: e.target.value.replace(/[^0-9]/g, "").slice(0, 16) })} data-testid="portal-cit-card" />
              <button className="bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded-sm hover:bg-emerald-600 disabled:opacity-50"
                disabled={!pay.payer_name.trim() || busy} onClick={payNow} data-testid="portal-cit-pay-btn">
                {busy ? "Processing…" : `Pay $${Number(cit.fine).toFixed(2)}`}
              </button>
            </div>
          )}
          {paid && <div className="mt-3 text-sm bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2 text-emerald-800" data-testid="portal-cit-receipt">
            Payment accepted. Receipt <b className="font-mono">{paid.receipt_number}</b> — ${Number(paid.amount).toFixed(2)} applied to case {paid.case_number}. Keep this number for your records.</div>}
        </div>
      )}
    </div>
  );
}

export default function PublicPortal() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [board, setBoard] = useState(null);
  const [boardDate, setBoardDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/portal/hearings`, { params: { date: boardDate } }).then((r) => setBoard(r.data)).catch(() => {});
  }, [boardDate]);

  const search = async (e) => {
    e?.preventDefault();
    setErr(null);
    try {
      const r = await axios.get(`${API}/portal/search`, { params: { q } });
      setResults(r.data);
    } catch (ex) { setErr(ex.response?.data?.detail || "Search failed"); setResults(null); }
  };

  return (
    <Shell>
      <div className="bg-white border border-stone-200 rounded-md p-6 shadow-sm" data-testid="portal-search-panel">
        <h1 className="text-2xl font-semibold tracking-tight mb-1">Case Lookup</h1>
        <p className="text-sm text-stone-500 mb-4">Search public court records by case number or party name. No account required.</p>
        <form onSubmit={search} className="flex gap-2">
          <input className="flex-1 border border-stone-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-stone-500"
            placeholder="e.g. 2026-CR-700001 or Thornton" value={q} onChange={(e) => setQ(e.target.value)} data-testid="portal-search-input" />
          <button type="submit" className="inline-flex items-center gap-1.5 bg-stone-900 text-white text-sm font-medium px-4 py-2 rounded-sm hover:bg-stone-700" data-testid="portal-search-btn">
            <Search className="w-4 h-4" /> Search
          </button>
        </form>
        {err && <div className="mt-3 text-sm text-red-700" data-testid="portal-search-error">{err}</div>}
        {results && (
          <div className="mt-4 divide-y divide-stone-100 border-t border-stone-200" data-testid="portal-results">
            {results.map((c) => (
              <button key={c.case_number} className="w-full text-left py-2.5 px-1 hover:bg-stone-50 flex items-center gap-3"
                onClick={() => navigate(`/portal/case/${encodeURIComponent(c.case_number)}`)} data-testid={`portal-result-${c.case_number}`}>
                <span className="font-mono text-sm text-blue-900">{c.case_number}</span>
                <span className="text-sm flex-1 truncate">{c.caption}</span>
                <Badge v={c.case_type} /><Badge v={c.status} />
              </button>
            ))}
            {!results.length && <p className="py-3 text-sm text-stone-500" data-testid="portal-no-results">No publicly available cases match.</p>}
          </div>
        )}
      </div>

      <div className="bg-white border border-stone-200 rounded-md p-6 shadow-sm mt-6" data-testid="portal-board">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <h2 className="text-lg font-semibold flex items-center gap-2"><CalendarDays className="w-5 h-5 text-stone-500" /> Court Calendar</h2>
          <input type="date" value={boardDate} onChange={(e) => setBoardDate(e.target.value)}
            className="border border-stone-300 rounded-sm px-2 py-1 text-sm ml-auto" data-testid="portal-calendar-date" />
        </div>
        {(() => {
          const groups = {};
          (board?.hearings || []).forEach((h) => { (groups[h.courtroom || "Unassigned courtroom"] ||= []).push(h); });
          const rooms = Object.keys(groups).sort();
          if (board && !rooms.length) return <p className="py-3 text-sm text-stone-500" data-testid="portal-calendar-empty">No public hearings scheduled on {board.date}.</p>;
          return rooms.map((room) => (
            <div key={room} className="mb-4" data-testid={`portal-room-${room.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
              <div className="text-[11px] font-bold uppercase tracking-wider text-stone-500 bg-stone-100 border border-stone-200 rounded-sm px-2 py-1 mb-1">{room}</div>
              <table className="w-full border-collapse text-sm">
                <thead><tr className="text-left text-[11px] uppercase tracking-wider text-stone-400 border-b border-stone-200">
                  <th className="py-1.5">Time</th><th>Case</th><th>Caption</th><th>Hearing</th><th>Status</th></tr></thead>
                <tbody>
                  {groups[room].map((h, i) => (
                    <tr key={`${h.case_number}-${h.time}-${i}`} className="border-b border-stone-100">
                      <td className="py-2 font-mono text-xs">{h.time}</td>
                      <td className="font-mono text-xs"><Link className="text-blue-900 hover:underline" to={`/portal/case/${encodeURIComponent(h.case_number)}`}>{h.case_number}</Link></td>
                      <td className="truncate max-w-xs">{h.caption}</td>
                      <td className="text-xs">{h.hearing_type}{h.remote_link && <a href={h.remote_link} target="_blank" rel="noreferrer" className="ml-1 inline-flex items-center text-[10px] font-bold px-1 rounded-sm bg-indigo-100 text-indigo-900" data-testid={`portal-remote-${h.case_number}`}>Remote ({h.remote_platform || "video"}) ↗</a>}</td>
                      <td><Badge v={h.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ));
        })()}
      </div>
      <CitationPay />
    </Shell>
  );
}

export function PublicCaseView() {
  const { caseNumber } = useParams();
  const [data, setData] = useState(null);
  const [docs, setDocs] = useState([]);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/portal/cases/${encodeURIComponent(caseNumber)}`)
      .then((r) => setData(r.data))
      .catch((ex) => setErr(ex.response?.data?.detail || "Not available"));
    axios.get(`${API}/portal/cases/${encodeURIComponent(caseNumber)}/documents`)
      .then((r) => setDocs(r.data)).catch(() => setDocs([]));
  }, [caseNumber]);
  useEffect(() => { load(); }, [load]);

  return (
    <Shell>
      <Link to="/portal" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:underline mb-4" data-testid="portal-back"><ArrowLeft className="w-4 h-4" /> Back to search</Link>
      {err && <div className="bg-white border border-stone-200 rounded-md p-6 text-sm text-red-700" data-testid="portal-case-error">{err}</div>}
      {data && (
        <div className="space-y-5" data-testid="portal-case-view">
          <div className="bg-white border border-stone-200 rounded-md p-6 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="font-mono text-sm text-stone-500">{data.case_number}</div>
                <h1 className="text-xl font-semibold tracking-tight">{data.caption}</h1>
              </div>
              <Badge v={data.case_type} /><Badge v={data.status} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
              <div><div className="text-[10px] uppercase tracking-wider text-stone-400">Filed</div><span className="font-mono text-xs">{data.filed_date}</span></div>
              {data.disposition?.label && <div className="col-span-2"><div className="text-[10px] uppercase tracking-wider text-stone-400">Disposition</div><span className="text-xs" data-testid="portal-disposition">{data.disposition.label} ({data.disposition.date})</span></div>}
            </div>
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-wider text-stone-400 mb-1">Parties</div>
              <div className="flex flex-wrap gap-2">
                {data.parties.map((p, i) => (
                  <span key={`${p.relationship_type}-${p.display_name}-${i}`} className="text-xs bg-stone-100 border border-stone-200 rounded-sm px-2 py-1">
                    <b>{p.relationship_type}</b>: {p.display_name}{p.attorney_name ? ` (Counsel: ${p.attorney_name})` : ""}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white border border-stone-200 rounded-md p-6 shadow-sm">
            <h2 className="text-base font-semibold mb-2">Hearings</h2>
            <table className="w-full border-collapse text-sm" data-testid="portal-hearings-table">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-stone-400 border-b border-stone-200"><th className="py-1.5">Date/Time</th><th>Type</th><th>Courtroom</th><th>Status</th><th>Result</th></tr></thead>
              <tbody>{data.hearings.map((h, i) => (
                <tr key={`${h.scheduled_start}-${h.hearing_type}-${i}`} className="border-b border-stone-100">
                  <td className="py-2 font-mono text-xs">{(h.scheduled_start || "").replace("T", " ").slice(0, 16)}</td>
                  <td className="text-xs">{h.hearing_type_label || h.hearing_type}</td>
                  <td className="text-xs">{h.courtroom}</td>
                  <td><Badge v={h.status} /></td>
                  <td className="text-xs">{h.result || "—"}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>

          <div className="bg-white border border-stone-200 rounded-md p-6 shadow-sm">
            <h2 className="text-base font-semibold mb-2">Public Docket</h2>
            <table className="w-full border-collapse text-sm" data-testid="portal-docket-table">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-stone-400 border-b border-stone-200"><th className="py-1.5">#</th><th>Date</th><th>Entry</th></tr></thead>
              <tbody>{data.docket.map((e) => (
                <tr key={e.sequence_number} className="border-b border-stone-100">
                  <td className="py-2 font-mono text-xs">{e.sequence_number}</td>
                  <td className="font-mono text-xs whitespace-nowrap">{e.date}</td>
                  <td className="text-xs">{e.description}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>

          <div className="bg-white border border-stone-200 rounded-md p-6 shadow-sm">
            <h2 className="text-base font-semibold mb-2">Public Documents</h2>
            {docs.length === 0 ? (
              <p className="text-sm text-stone-500" data-testid="portal-docs-empty">No documents on this case have been released for public access. Certified copies are available from the Clerk of Court.</p>
            ) : (
              <table className="w-full border-collapse text-sm" data-testid="portal-docs-table">
                <thead><tr className="text-left text-[11px] uppercase tracking-wider text-stone-400 border-b border-stone-200"><th className="py-1.5">Filed</th><th>Title</th><th>Type</th><th></th></tr></thead>
                <tbody>{docs.map((d) => (
                  <tr key={d.id} className="border-b border-stone-100">
                    <td className="py-2 font-mono text-xs whitespace-nowrap">{(d.uploaded_at || "").slice(0, 10)}</td>
                    <td className="text-xs">{d.title}{d.version > 1 && <span className="ml-1 text-[10px] font-mono text-stone-400">v{d.version}</span>}</td>
                    <td className="text-xs">{d.document_type}</td>
                    <td className="text-right">
                      <a className="text-xs text-blue-900 underline" href={`${API}/portal/documents/${d.id}/download`}
                        target="_blank" rel="noreferrer" data-testid={`portal-doc-view-${d.id}`}>View</a>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>

          <div className="text-[11px] text-stone-400 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> {data.notice}</div>
        </div>
      )}
    </Shell>
  );
}
